/**
 * 动态路由辅助工具（唯一实现）
 * 使用方：permission.js 守卫（落地/恢复/命名去重）、Breadcrumb 首页、TagsView 首标签判断
 * 原则：路由地址一律从动态路由树解析，不在业务代码里写死固定路径（如 /index）
 */

/** 拼接父子路由路径（与 vue-router 及 TopNav 的解析规则一致） */
function joinPath(base, path) {
  if (!path) return base || "/";
  if (/^(https?:)?\/\//.test(path)) return path;
  if (path.startsWith("/")) return path;
  if (!base || base === "/") return "/" + path;
  return base.replace(/\/+$/, "") + "/" + path;
}

/**
 * 展平动态路由树为叶子清单
 * @param {Array} routes 路由数组
 * @param {String} base 父路径
 * @param {Boolean} skipHidden 是否跳过 hidden===true 的子树（默认是）
 */
export function flattenLeaves(routes, base = "", acc = [], skipHidden = true) {
  (routes || []).forEach((route) => {
    if (!route) return;
    if (skipHidden && route.hidden === true) return;
    const full = joinPath(base, route.path);
    if (route.children && route.children.length) {
      flattenLeaves(route.children, full, acc, skipHidden);
    } else if (route.meta && route.meta.title && !isExternalPath(full)) {
      // 外链菜单（path 为 http(s)://）不参与「可渲染叶子」：守卫拿它做落地会拼成
      // <origin>/http://... => 必然 404（2026-10-21 线上实测：园区/一卡通首页即外链）。
      acc.push({
        path: full,
        query: route.query,
        title: route.meta.title,
        icon: route.meta.icon,
        name: route.name,
        // 组件是否真实命中（filterAsyncRouter 后 loadView 未命中 => undefined）。
        // 落地/恢复/自救一律跳过 componentOk=false 的叶子，防止落进空白页（主人定调：禁止 404 死循环）。
        componentOk: !!route.component,
      });
    }
  });
  return acc;
}

/** 首个可渲染叶子（登录后落地/404 自救用）：外链已排除 + component 必须命中 */
export function findFirstLeaf(routes) {
  return flattenLeaves(routes).find((leaf) => leaf.componentOk !== false) || null;
}

/** 动态解析「首页」路径：只认标题=「首页」且组件可渲染的叶子；找不到返回 ""（不拿别的页面冒充首页）。
 *  首页是外链/断链时返回空，由调用方静默降级到首个可渲染页（外链首页被忽略自动跳过）。 */
export function getHomePath(routes) {
  const home = flattenLeaves(routes).find(
    (leaf) => leaf.title === "首页" && leaf.componentOk !== false
  );
  return home ? home.path : "";
}

/** 路径是否存在于给定动态路由树且可渲染（sessionStorage 恢复前校验，防跨项目残留 404/空白页） */
export function hasRoutePath(routes, path) {
  if (!path) return false;
  return flattenLeaves(routes).some(
    (leaf) => leaf.path === path && leaf.componentOk !== false
  );
}

/** 由路径派生一个合法路由名（去掉非单词字符 + 首字母大写） */
function toRouteName(segment) {
  return String(segment || "")
    .replace(/[^\w]/g, "")
    .replace(/^[a-z]/, (c) => c.toUpperCase());
}

/** 外链地址：前端不注册该路由，也不占用路由名 */
function isExternalPath(path) {
  return /^(https?:)?\/\//.test(String(path || ""));
}

/**
 * 路由名补全/查重（2026-10-20 改版：撞名不再中止整棵树注册）
 * 旧实现遇到重名直接 throw，而它跑在 addRoute 循环之前 ⇒ 本次登录后所有动态路由都没注册 ⇒
 * 首页/返回全打不开、重新登录仍报同一错（一卡通项目两条 path=product 的重复菜单触发）。
 * 新口径（错误仍显式呈现，只是不再拖垮全站）：
 * ① 外链、以及 path 无 ASCII 可派生的节点 → 不命名（vue-router 允许无 name，按 path 照常可达）
 * ② 自身 path 派生撞名 → 改用「完整路径」派生，保住这条路由有名可用
 *    （业务里存在 router.push({name}) 硬编码依赖，禁止直接置空）
 * ③ 完整路径也相同 = 真重复菜单 → 不命名 + 记入返回值，由守卫明确报错提示修菜单
 * @returns {Array} 冲突清单 [{ name, path }]
 */
export function normalizeRouteNames(routes, usedNames) {
  const used = usedNames instanceof Set ? usedNames : new Set(usedNames || []);
  const conflicts = [];
  const walk = (list, base) => {
    (list || []).forEach((route) => {
      const full = joinPath(base, route.path);
      if (route.children && route.children.length) walk(route.children, full);
      if (route.name === undefined || route.name === null) return;
      if (isExternalPath(route.path)) {
        route.name = "";
        return;
      }
      const childName =
        route.children && route.children[0] && route.children[0].name
          ? String(route.children[0].name)
          : "";
      let name = String(route.name);
      if (name === "") name = toRouteName(route.path) || (childName ? childName + "Frame" : "");
      if (name && used.has(name)) name = toRouteName(full);
      if (name && used.has(name)) {
        conflicts.push({ name, path: full });
        route.name = "";
        return;
      }
      if (name) used.add(name);
      route.name = name;
    });
  };
  walk(routes, "");
  return conflicts;
}
